import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import styled from '@emotion/styled';
import { segment } from '@actions/analytics';
import LinkTag from '@base/Link/Link';
import { DOW2_AAD_STARTED } from '@constants/segment';
import useGoalInfo from '@api/hooks/goal/useGoalInfo';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn } from '@api/selectors/auth';
import { useSelectedGoalUID } from '@api/hooks/topology/useSelectedGoal';
import ParentProgressFloater from '@cont/Settings/ParentProgressFloater';
import useShouldShowParentProgressFloater from '@cont/Settings/useShouldShowParentProgressFloater';
import { useShouldShowSalesBotFab } from '@cont/SalesBot/Host/GlobalSalesBotFab';
import useGoalEventProps from '@hooks/useGoalEventProps';
import useLearnerFabClusterStore from '@stores/learnerFabCluster';
import useDarkModeStatus from '@stores/useDarkModeStatus';
import { getIsDarkModeOn } from '@stores/selectors/darkModeStatusSelectors';
import { mediaQuery, Spacings } from '@styles/index';
import { getImage } from '@utils/imageHelper';

type LearnerFabClusterProps = {
  inEducatorSide?: boolean;
  /** Goal pages that used to render the Ai Doubts pill (new goal layout, not store). */
  showAiDoubtsSurface?: boolean;
  /** Goal pages and planner, where the parent-progress floater used to render. */
  showParentSurface?: boolean;
};

const Cluster = styled.div`
  position: fixed;
  right: ${Spacings.SPACING_6B};
  bottom: ${Spacings.SPACING_45B};
  z-index: 1100;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 11px;

  ${mediaQuery.uptoMobile} {
    bottom: ${Spacings.SPACING_26B};
  }
`;

const Menu = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 11px;
`;

const CHIP_BORDER = {
  parent: 'var(--color-green-2)',
  doubt: 'var(--color-i-red-3)',
  chat: 'var(--color-accent-blue)'
} as const;

type ChipTone = keyof typeof CHIP_BORDER;

const chipStyles = ({ $tone }: { $tone: ChipTone }) => `
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 6px;
  width: 209px;
  margin: 0;
  padding: 4px 12px 4px 6px;
  border: 1px solid ${CHIP_BORDER[$tone]};
  border-radius: 1000px;
  background: var(--color-base-1);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.06);
  color: var(--color-text-primary);
  font-size: 16px;
  font-weight: 600;
  line-height: 18px;
  white-space: nowrap;
  cursor: pointer;
  text-decoration: none;
`;

const MenuButton = styled.button<{ $tone: ChipTone }>`
  ${chipStyles}
`;

const MenuLink = styled(LinkTag)<{ $tone: ChipTone }>`
  ${chipStyles}
`;

const IconWell = styled.span<{ $kind: 'parent' | 'chat' }>`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${({ $kind }) =>
    $kind === 'chat'
      ? 'var(--color-fab-chat-well)'
      : 'var(--color-fab-parent-well)'};
`;

const Glyph = styled.img`
  display: block;
  flex-shrink: 0;
`;

const Toggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: transparent;
  line-height: 0;
  cursor: pointer;
`;

const LearnerFabCluster = ({
  inEducatorSide = false,
  showAiDoubtsSurface = false,
  showParentSurface = false
}: LearnerFabClusterProps) => {
  const router = useRouter();
  const query = router?.query ?? {};
  const routeGoalUid = typeof query.goalUID === 'string' ? query.goalUID : '';
  const selectedGoalUid = useSelectedGoalUID();
  const goalUID = routeGoalUid || selectedGoalUid;
  const routeGoalSlug =
    typeof query.goalSlug === 'string' ? query.goalSlug : '';
  const { goalInfo } = useGoalInfo(goalUID);
  const goalSlug = goalInfo?.slug || routeGoalSlug;
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const isAiDoubtsEnabled = goalInfo?.isAiDoubtsEnabled === true;

  const [parentOpenRequestId, setParentOpenRequestId] = useState(0);
  const isMenuOpen = useLearnerFabClusterStore((state) => state.isMenuOpen);
  const setMenuOpen = useLearnerFabClusterStore((state) => state.setMenuOpen);
  const closeTtuTooltip = useLearnerFabClusterStore(
    (state) => state.closeTtuTooltip
  );
  const parentAllowed = useShouldShowParentProgressFloater(goalUID);
  const showParent = showParentSurface && parentAllowed;
  const showAiDoubts =
    showAiDoubtsSurface &&
    !!goalSlug &&
    !!goalUID &&
    (isAiDoubtsEnabled || !isLoggedIn);
  const showSalesBot = useShouldShowSalesBotFab(inEducatorSide);
  const isDarkModeOn = useDarkModeStatus(getIsDarkModeOn);
  const openSalesBot = useLearnerFabClusterStore((state) => state.openSalesBot);
  const routeGoalSegmentData = useGoalEventProps({ goalUID });

  useEffect(() => () => setMenuOpen(false), [setMenuOpen]);

  if (!showParent && !showAiDoubts && !showSalesBot) return null;

  const openParentContact = () => {
    setMenuOpen(false);
    setParentOpenRequestId((id) => id + 1);
  };

  const openChat = () => {
    setMenuOpen(false);
    openSalesBot?.();
  };

  const toggleMenu = () => {
    if (!isMenuOpen) closeTtuTooltip?.();
    setMenuOpen(!isMenuOpen);
  };

  return (
    <>
      <Cluster>
        {isMenuOpen ? (
          <Menu>
            {showParent ? (
              <MenuButton
                type="button"
                $tone="parent"
                onClick={openParentContact}
              >
                <IconWell $kind="parent">
                  <Glyph
                    src={getImage('fab-cluster/parent-glyph.svg')}
                    alt=""
                  />
                </IconWell>
                Add parent contact
              </MenuButton>
            ) : null}
            {showAiDoubts ? (
              <MenuLink
                href="/goal/[goalSlug]/[goalUID]/doubts-and-solutions/unacademy-ai"
                as={`/goal/${goalSlug}/${goalUID}/doubts-and-solutions/unacademy-ai`}
                $tone="doubt"
                onClick={() => {
                  segment.track(DOW2_AAD_STARTED, {
                    last_primary_source_section: 'Menu Tab',
                    click_a_picture_clicked: 'No',
                    feature_version: 2,
                    doubt_source: 'Ask AI Floater',
                    doubt_type: 'Doubts AI',
                    ...routeGoalSegmentData
                  });
                }}
              >
                <Glyph
                  src={getImage(
                    isDarkModeOn
                      ? 'fab-cluster/ask-doubt-dark.svg'
                      : 'fab-cluster/ask-doubt-light.svg'
                  )}
                  alt=""
                />
                Ask a doubt
              </MenuLink>
            ) : null}
            {showSalesBot ? (
              <MenuButton type="button" $tone="chat" onClick={openChat}>
                <IconWell $kind="chat">
                  <Glyph src={getImage('fab-cluster/chat-glyph.svg')} alt="" />
                </IconWell>
                Chat with us
              </MenuButton>
            ) : null}
          </Menu>
        ) : null}
        <Toggle
          type="button"
          aria-expanded={isMenuOpen}
          aria-label={isMenuOpen ? 'Close shortcuts' : 'Open shortcuts'}
          onClick={toggleMenu}
        >
          <Glyph
            src={getImage(
              isMenuOpen
                ? 'fab-cluster/fab-close.svg'
                : 'fab-cluster/fab-open.svg'
            )}
            alt=""
          />
        </Toggle>
      </Cluster>
      {showParent ? (
        <ParentProgressFloater
          goalUID={goalUID}
          openRequestId={parentOpenRequestId}
        />
      ) : null}
    </>
  );
};

export default LearnerFabCluster;
